import React, { useRef, useEffect } from 'react';
import styled from 'styled-components';
import { useMediaQuery } from 'planoplan-ui-kit/core/hooks/useMediaQuery';
import { Colors } from 'planoplan-ui-kit/core/colors';
import { Button } from 'planoplan-ui-kit/core/button';
import { useTranslations } from '@libs/translations';
import { SectionNumber } from '@components/Section-Number';
import { Separator } from '@components/Separator';
import { SectionAnchor } from '@components/Section-Anchor';
import { Points } from '@styles';

export const FinalTouch = () => {
  const translations = useTranslations();
  const isTabletPortrait = useMediaQuery(Points.tablet_landscape, { useSSR: true });
  const isDesktopWide = useMediaQuery(Points.laptop_wide, { useSSR: true });
  const wrapperRef = useRef(null);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;

    const handleScroll = () => {
      const rect = el.getBoundingClientRect();
      const windowHeight = window.innerHeight;

      const start = windowHeight;
      const end = windowHeight * 0.4;

      const progress = (start - rect.top) / (start - end);
      const clampedProgress = Math.min(Math.max(progress, 0), 1);

      el.style.setProperty('--scroll-progress', String(clampedProgress));
    };

    handleScroll();

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, []);

  return (
    <View id="final-touch">
      <Content>
        <WrapperTop>
          <SectionNumber number={''} text={translations.t('site.home.final-touch.name')} />
          <Title>{translations.t('site.home.final-touch.title')}</Title>
          <Description>{translations.t('site.home.final-touch.description')}</Description>
        </WrapperTop>
        <SeparatorWrapper>
          <Separator />
        </SeparatorWrapper>
        <WrapperBottom>
          <Text dangerouslySetInnerHTML={{ __html: translations.t('site.home.final-touch.text') }}></Text>
        </WrapperBottom>
        <ActionWrapper ref={wrapperRef}>
          <ButtonWrapper>
            <Button
              theme="neutral_secondary"
              data-gtm="button_click"
              widthText="true"
              size={isDesktopWide ? 'L' : isTabletPortrait ? 'M' : 'S'}
              href={translations.t('site.home.final-touch.project-link')}
              target="_blank">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M1.3335 8.00016C1.3335 8.00016 3.75774 3.3335 8.00016 3.3335C12.2426 3.3335 14.6668 8.00016 14.6668 8.00016C14.6668 8.00016 12.2426 12.6668 8.00016 12.6668C3.75774 12.6668 1.3335 8.00016 1.3335 8.00016Z"
                  stroke="#292929"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M8 10C9.10457 10 10 9.10457 10 8C10 6.89543 9.10457 6 8 6C6.89543 6 6 6.89543 6 8C6 9.10457 6.89543 10 8 10Z"
                  stroke="#292929"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              {translations.t('site.home.final-touch.view-example')}
            </Button>
            <SectionAnchor text={translations.t('site.home.final-touch.anchor')} isHeartMode={true} />
          </ButtonWrapper>
        </ActionWrapper>
      </Content>
    </View>
  );
};

const View = styled.section`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 30px 16px 0;
  background-color: ${Colors.main_beige};

  @media ${Points.tablet_landscape} {
    padding: 40px 0 0;
  }

  @media ${Points.laptop_wide} {
    padding: min(90px, 9.375vh) 80px 0;
  }

  @media ${Points.fullhd} {
    padding: min(100px, 9.375vh) 160px 0;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;

  @media ${Points.tablet_landscape} {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-template-areas:
      'top bottom'
      'separator separator'
      'action action';
    gap: 0 38px;
    width: 100%;
    max-width: 1024px;
    margin: 0 auto;
    padding: 0 28px;
  }

  @media ${Points.laptop_wide} {
    grid-template-columns: 592px 518px;
    gap: 0 clamp(169px, calc(100% - 592px - 518px), 490px);
    width: 100%;
    max-width: 1440px;
    padding: 0;
  }

  @media ${Points.fullhd} {
    gap: 0 490px;
    max-width: 1600px;
    padding: 0;
    margin: 0;
  }
`;

const WrapperTop = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;

  @media ${Points.tablet_landscape} {
    grid-area: top;
    align-items: start;
    margin-bottom: min(31px, 5vh);
  }

  @media ${Points.laptop_wide} {
    margin-bottom: min(31px, 3.2vh);
  }

  @media ${Points.fullhd} {
    margin-bottom: min(20px, 2.08vh);
  }
`;

const WrapperBottom = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;

  @media ${Points.tablet_landscape} {
    grid-area: bottom;
    margin-bottom: min(31px, 5vh);
  }

  @media ${Points.laptop_wide} {
    margin-bottom: 0;
  }

  @media ${Points.fullhd} {
    margin-bottom: min(20px, 2.083vh);
  }
`;

const SeparatorWrapper = styled.div`
  @media ${Points.tablet_landscape} {
    grid-area: separator;
  }
`;

const ActionWrapper = styled.div`
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: calc(100% + 32px);
  margin: 0 -16px;
  padding: 0 16px;
  aspect-ratio: 100 / 63;
  --scroll-progress: 0;
  background: image-set(
      url('/images/final-touch-bg-mob.webp') type('image/webp'),
      url('/images/final-touch-bg-mob.jpg') type('image/jpeg')
    )
    0 0 / 100% no-repeat;

  @media ${Points.tablet_landscape} {
    grid-area: action;
    justify-content: flex-start;
    aspect-ratio: 100 / 45;
    width: 100%;
    margin: 0;
    margin-top: min(41px, 4.16vh);
    padding: 0;
    background: image-set(
          url('/images/final-touch-bg-1x.webp') 1x type('image/webp'),
          url('/images/final-touch-bg-1x.jpg') 1x type('image/jpeg'),
          url('/images/final-touch-bg-2x.webp') 2x type('image/webp'),
          url('/images/final-touch-bg-2x.jpg') 2x type('image/jpeg')
        )
        0 0 / 99% no-repeat,
      linear-gradient(#f4f4f4, #f4f4f4) right 0.5% top calc(20px - (13px * var(--scroll-progress))) / 14.58% 100%
        no-repeat,
      linear-gradient(#dedede, #dedede) right 0 top calc(40px - (28px * var(--scroll-progress))) / 14.58% 100% no-repeat;
  }

  @media ${Points.laptop_wide} {
    aspect-ratio: 100 / 46.67;
    margin-top: min(30px, 4.16vh);
  }

  @media ${Points.fullhd} {
    aspect-ratio: 100 / 39.25;
  }
`;

const ButtonWrapper = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: min(40px, 6vh);
  width: 100%;

  svg {
    margin-right: 8px;
  }

  @media ${Points.tablet_landscape} {
    justify-content: start;
    gap: min(96px, 10.08vh);
    margin-top: min(121px, 17vh);
  }

  @media ${Points.laptop_wide} {
    gap: min(150px, 15.625vh);
    margin-top: clamp(24px, calc(52vh - 226px), 273px);
  }

  @media ${Points.fullhd} {
    gap: min(135px, 14.0625vh);
    margin-top: clamp(24px, calc(52vh - 280px), 282px);
  }
`;

const Title = styled.h2`
  margin-top: -5px;
  margin-bottom: min(15px, 1.56vh);
  font-weight: 400;
  font-size: clamp(20px, 3.9vh, 26px);
  line-height: 1;
  letter-spacing: 0%;
  font-variant-numeric-fraction: stacked-fractions;

  @media ${Points.tablet_landscape} {
    font-size: clamp(26px, 5.5vh, 34px);
  }

  @media ${Points.laptop_wide} {
    font-size: clamp(34px, 5.6vh, 54px);
  }

  @media ${Points.fullhd} {
    font-size: 54px;
  }
`;

const Description = styled.p`
  font-weight: 400;
  font-size: 12px;
  line-height: 1.1;
  letter-spacing: 1%;

  @media ${Points.laptop_wide} {
    font-size: 14px;
  }
`;

const Text = styled.div`
  font-weight: 400;
  font-size: clamp(13px, 2.4vh, 16px);
  line-height: 1.3;
  letter-spacing: 0%;
  font-variant-numeric-fraction: stacked-fractions;
  -webkit-text-size-adjust: 100%;

  p:not(:last-child) {
    margin-bottom: min(15px, 1.6vh);
  }

  p:last-child {
    margin-bottom: min(30px, 3.1vh);
  }

  @media ${Points.tablet_landscape} {
    font-size: clamp(14px, 2.6vh, 16px);
  }

  @media ${Points.laptop_wide} {
    font-size: clamp(16px, 1.875vh, 18px);
  }
`;
